/**
 * Campaigns — the marketing landing page.
 *
 * Three tabs: every campaign, the analytics that follow from what was actually
 * sent, and the channel setup.
 */
import { useMemo, useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import Markdown from '../../lib/markdown.jsx';
import {
  Card, CardHead, CardBody, EmptyState, Avatar, Dash, Pill, AIBadge, Spinner, ProgressBar
} from '../../components/Primitives.jsx';
import {
  CHANNELS, channelLabel, channelIcon
} from '../../constants/marketing.js';
import {
  campaignStats, channelBreakdown, scheduleState, marketingTotals
} from '../../domain/marketing.js';
import { ChannelPill, CampaignStatusPill, ChannelConfig, OptOutEditor } from './MarketingBits.jsx';
import CampaignFilters, { NO_FILTERS, applyFilters } from './CampaignFilters.jsx';
import MailSetup from './MailSetup.jsx';

const TABS = [
  ['campaigns', 'All campaigns', 'mega'],
  ['analytics', 'Analytics', 'chart'],
  ['channels', 'Channel setup', 'gear']
];

/**
 * The headline strip: what the module did, then the same split by channel.
 *
 * Every figure here is counted from what actually happened. "Delivered" means
 * the mail server accepted the message, which is the only delivery fact an
 * SMTP send knows — and read and click rates are left blank, marked with what
 * they would need, rather than filled with a plausible number. A made-up open
 * rate is worse than an empty one: campaigns get planned around it.
 */
const HEAD = [
  ['campaigns', 'Campaigns', 'mega', 'pink', t => t.camps],
  ['sent', 'Total sent', 'send', 'blue', t => t.sent.toLocaleString()],
  ['delivery', 'Delivery rate', 'check', 'green', t => (t.delivery === null ? '—' : `${t.delivery}%`)],
  ['replied', 'Replied', 'chat', 'purple', t => t.replied],
  // The last two are states rather than categories, so their value is allowed
  // to take the colour — but only when there is something to report.
  ['bounced', 'Bounced', 'alert', 'amber', t => t.bounced, t => t.bounced > 0],
  ['failed', 'Failed', 'x', 'red', t => t.failed, t => t.failed > 0]
];

/* The same two, once the recipients' mail clients can reach us. */
const TRACKED = [
  ['opened', 'Read / open', 'eye', 'purple', t => (t.openRate === null ? '—' : `${t.openRate}%`)],
  ['clicked', 'Click rate', 'target', 'blue', t => (t.clickRate === null ? '—' : `${t.clickRate}%`)]
];

const NEEDS = [
  ['Read / open', 'eye', 'needs PUBLIC_URL set'],
  ['Click rate', 'target', 'needs PUBLIC_URL set']
];

export function MarketingTiles() {
  const { mktCtx, setSheet, tracking } = useApp();
  /* Every campaign's stats scan the message logs; without this the whole lot
     is recomputed on any render, including opening an unrelated popup. */
  const t = useMemo(() => marketingTotals(mktCtx), [mktCtx]);

  return (
    <div className="mhead">
      {HEAD.map(([metric, label, icon, tone, read, hit]) => (
        /* Each figure opens the rows it counted. A tile that says "Bounced 1"
           and cannot be opened is a dead end — the only useful next question
           is which one. */
        <button
          type="button"
          className={`mtile t-${tone}${hit && hit(t) ? ' hit' : ''}`}
          key={metric}
          onClick={() => setSheet({ kind: 'marketingDetail', metric })}
        >
          <span className="mtop">
            <small>{label}</small>
            <span className="mic"><Icon name={icon} /></span>
          </span>
          <b>{read(t)}</b>
          <span className="mgo">See the {label.toLowerCase()}<Icon name="chev" /></span>
        </button>
      ))}

      {tracking ? TRACKED.map(([metric, label, icon, tone, read]) => (
        <button
          type="button"
          className={`mtile t-${tone}`}
          key={metric}
          onClick={() => setSheet({ kind: 'marketingDetail', metric })}
        >
          <span className="mtop">
            <small>{label}</small>
            <span className="mic"><Icon name={icon} /></span>
          </span>
          <b>{read(t)}</b>
          <span className="mgo">See who<Icon name="chev" /></span>
        </button>
      )) : NEEDS.map(([label, icon, why]) => (
        <div className="mtile need" key={label}>
          <span className="mtop">
            <small>{label}</small>
            <span className="mic"><Icon name={icon} /></span>
          </span>
          <b>—</b>
          <em>{why}</em>
        </div>
      ))}
    </div>
  );
}

function CampaignTable({ list }) {
  const { mkt, mktCtx, setSheet, removeMarketingRecord } = useApp();

  if (!list.length) {
    return (
      <EmptyState icon="mega" tone="g-pink" title="Nothing to show">
        No campaign matches those filters — or there are none yet. Write once and send it
        across any channel.
      </EmptyState>
    );
  }

  const remove = (e, id) => {
    e.stopPropagation();
    // eslint-disable-next-line no-alert
    if (window.confirm('Delete this campaign?')) removeMarketingRecord('campaigns', id);
  };

  return (
    <div className="tscroll">
      <table className="camptbl">
        <thead>
          <tr>
            <th>Campaign</th><th>Channel</th><th>Goal</th><th>Audience</th><th>Status</th>
            <th>Reach</th><th>Sent</th><th>Replied</th><th>Converted</th><th>When</th><th />
          </tr>
        </thead>
        <tbody>
          {list.map(c => {
            const st = campaignStats(c, mktCtx);
            const audience = mkt.audiences.find(a => a.id === c.audience);
            const due = scheduleState(c);
            return (
              <tr key={c.id} onClick={() => setSheet({ kind: 'campaignView', id: c.id })}>
                <td>
                  <div className="cname">
                    <Avatar name={c.name} />
                    <div><b>{c.name}</b><small>{c.subject || ''}</small></div>
                  </div>
                </td>
                <td><ChannelPill channel={c.channel} /></td>
                <td>{c.goal ? <Pill tone="p-purple">{c.goal}</Pill> : <Dash />}</td>
                <td className="dim">{audience
                          ? audience.name
                          : (c.contacts || []).length
                            ? `${(c.contacts || []).length} chosen`
                            : '—'}</td>
                <td><CampaignStatusPill status={c.status} /></td>
                <td><b>{st.reach}</b></td>
                <td>{st.sent}</td>
                <td>{st.replied}</td>
                <td style={{ color: st.converted ? 'var(--green)' : 'var(--ink3)' }}><b>{st.converted}</b></td>
                <td>
                  <div className="whenc">
                    <span className="dim">{c.schedule || c.created || ''}</span>
                    {due ? <span className={'duep ' + due.tone}>{due.label}</span> : null}
                  </div>
                </td>
                <td>
                  <div className="acts">
                    {/* Once something has gone out, the same button is a
                        different act: sending again, not sending. */}
                    <button
                      type="button"
                      title={(c.sends || []).some(x => x.status === 'sent')
                        ? 'Send it again'
                        : 'Open the send console'}
                      onClick={e => { e.stopPropagation(); setSheet({ kind: 'send', id: c.id }); }}
                    >
                      <Icon name={(c.sends || []).some(x => x.status === 'sent') ? 'refresh' : 'send'} />
                    </button>
                    <button
                      type="button"
                      title="Campaign details"
                      onClick={e => { e.stopPropagation(); setSheet({ kind: 'campaignView', id: c.id }); }}
                    >
                      <Icon name="eye" />
                    </button>
                    <button
                      type="button"
                      title="Edit"
                      onClick={e => { e.stopPropagation(); setSheet({ kind: 'campaign', id: c.id }); }}
                    >
                      <Icon name="edit" />
                    </button>
                    <button type="button" className="rm" title="Delete" onClick={e => remove(e, c.id)}>
                      <Icon name="trash" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * One channel's card.
 *
 * The four figures are a funnel, so they read as one: reached, sent, replied,
 * converted. The meter under them is the reply rate, and its track is a light
 * step of the channel's own hue so the state reads across the whole bar rather
 * than only where it is filled.
 *
 * A channel nobody has used says so, instead of showing four zeros — a wall of
 * zeros looks like a broken screen, not an unused channel.
 */
function ChannelCard({ channel, label, stats }) {
  const rate = stats.sent ? Math.round(stats.replied / stats.sent * 100) : null;
  const used = stats.camps > 0;

  return (
    <article className={`chcard ch-${channel}${used ? '' : ' idle'}`}>
      <header>
        <span className="chdot"><Icon name={channelIcon(channel)} /></span>
        <b>{label}</b>
        <span className="chcamps">
          {stats.camps ? `${stats.camps} campaign${stats.camps > 1 ? 's' : ''}` : 'unused'}
        </span>
      </header>

      {used ? (
        <>
          <div className="chbig">
            <span>{stats.sent}</span>
            <small>messages sent</small>
          </div>

          <dl className="chfunnel">
            <div><dt>Reached</dt><dd>{stats.reach}</dd></div>
            <div><dt>Replied</dt><dd>{stats.replied}</dd></div>
            <div><dt>Converted</dt><dd className={stats.converted ? 'good' : ''}>{stats.converted}</dd></div>
          </dl>

          <div className="chmeter" title={`${stats.replied} replies from ${stats.sent} sent`}>
            <div className="chtrack">
              <div className="chfill" style={{ width: `${rate === null ? 0 : Math.max(rate, 2)}%` }} />
            </div>
            <span>{rate === null ? <Dash /> : `${rate}% replied`}</span>
          </div>
        </>
      ) : (
        <p className="chidle">Nothing has gone out on {label} yet.</p>
      )}
    </article>
  );
}

function Analytics() {
  const { mkt, mktCtx, fmtMoney } = useApp();
  const byChannel = channelBreakdown(mktCtx);
  const finished = mkt.campaigns.filter(c => (c.sends || []).some(s => s.status === 'sent'));

  const blank = { camps: 0, reach: 0, sent: 0, replied: 0, converted: 0, revenue: 0 };
  const total = CHANNELS.reduce((t, [k]) => {
    const v = byChannel[k] || blank;
    return {
      camps: t.camps + v.camps,
      reach: t.reach + v.reach,
      sent: t.sent + v.sent,
      replied: t.replied + v.replied,
      converted: t.converted + v.converted,
      revenue: t.revenue + v.revenue
    };
  }, blank);

  const replyRate = total.sent ? Math.round(total.replied / total.sent * 100) : null;

  return (
    <>
      <div className="set">
        {/* One hero figure for the view: what actually left the building. The
            rest are supporting tiles, because three competing big numbers read
            as three dashboards. */}
        <div className="anhero">
          <div className="anlead">
            <span className="anfig">{total.sent}</span>
            <small>messages sent across {total.camps} campaign{total.camps === 1 ? '' : 's'}</small>
          </div>

          <div className="antiles">
            <div className="antile t-blue">
              <i><Icon name="chat" /></i>
              <span>{replyRate === null ? '—' : `${replyRate}%`}</span>
              <small>Reply rate</small>
            </div>
            <div className="antile t-purple">
              <i><Icon name="users" /></i>
              <span>{total.reach}</span>
              <small>Reachable now</small>
            </div>
            <div className={`antile t-green${total.converted ? ' good' : ''}`}>
              <i><Icon name="check" /></i>
              <span>{total.converted}</span>
              <small>Converted after</small>
            </div>
            <div className="antile t-amber">
              <i><Icon name="card" /></i>
              <span>{fmtMoney(total.revenue)}</span>
              <small>Revenue after</small>
            </div>
          </div>
        </div>

        <p className="annote">
          Sent and replied come from what the console actually logged. Converted counts
          recipients who paid, or moved to a paid plan, after the campaign went out — the only
          conversion signal that is real without tracking pixels.
        </p>
      </div>

      <div className="set">
        <h4>By channel</h4>
        <div className="chgrid">
          {CHANNELS.map(([k, label]) => (
            <ChannelCard key={k} channel={k} label={label} stats={byChannel[k] || blank} />
          ))}
        </div>
      </div>

      <div className="set">
        <h4>Campaign results</h4>
        {finished.length ? (
          <div className="tscroll">
            <table className="antbl">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th className="num">Sent</th>
                  <th>Progress</th>
                  <th className="num">Replied</th>
                  <th className="num">Converted</th>
                  <th className="num">Revenue after</th>
                </tr>
              </thead>
              <tbody>
                {finished.map(c => {
                  const st = campaignStats(c, mktCtx);
                  const pct = st.reach ? Math.round(st.sent / st.reach * 100) : 0;
                  return (
                    <tr key={c.id}>
                      <td>
                        <div className="ancamp">
                          <span className={`chdot ch-${c.channel}`}>
                            <Icon name={channelIcon(c.channel)} />
                          </span>
                          <div>
                            <b>{c.name}</b>
                            <small>{channelLabel(c.channel)} · {c.sentAt || c.created || ''}</small>
                          </div>
                        </div>
                      </td>
                      <td className="num">{st.sent} <span className="of">of {st.reach}</span></td>
                      <td style={{ minWidth: 170 }}><ProgressBar pct={pct} label={`${pct}%`} /></td>
                      <td className="num">{st.replied || <Dash />}</td>
                      <td className={`num${st.converted ? ' good' : ''}`}>{st.converted || <Dash />}</td>
                      <td className="num strong">{st.revenue ? fmtMoney(st.revenue) : <Dash />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="chart" tone="g-cyan" title="Nothing has been sent yet">
            Open a campaign&rsquo;s send console and the figures start here.
          </EmptyState>
        )}
      </div>
    </>
  );
}

/*
 * Channel setup used to be one long scroll: email, WhatsApp, SMS, push, the
 * sending rules and the opt-out list, stacked. Nobody configures four channels
 * at once — they come here to fix one — so each is its own pane behind a
 * segmented control, and the screen opens on the one being worked.
 *
 * The active pill wears the channel's own colour, which is the same hue the
 * campaign pills and the analytics meters use, so a channel looks like itself
 * everywhere. Colour is never the only signal: every pill carries its icon and
 * its name.
 */
const SETUP_PANES = [
  ['email', 'Email', 'mail'],
  ['whatsapp', 'WhatsApp', 'chat'],
  /* SMS is off the tabs: nothing is sent through it and there is no account
     to set up, so it was a settings pane for a channel nobody uses. The
     pane and its fields remain for when there is one. */
  ['push', 'Push', 'bell'],
  ['rules', 'Sending rules', 'gear'],
  ['optouts', 'Opt-outs', 'alert']
];

/** What each channel pane says about itself, above the fields. */
const CHANNEL_BLURB = {
  email: 'The mailboxes campaigns go out through, in the order they are tried.',
  whatsapp: 'Connected through the Meta Cloud API. Business-initiated messages outside a 24-hour window need an approved template.',
  sms: 'Sender IDs in the UAE must be registered with your aggregator, and marketing SMS needs recorded consent under TDRA rules.',
  push: 'Notifications to the XYRA apps, through Firebase. Connect the project and name a topic below.'
};

function ChannelSetup() {
  const { S, mkt, setChannelSetting } = useApp();
  const [pane, setPane] = useState('email');
  const M = S.mkt || {};

  const toggle = (key, label, hint, defaultOn) => (
    <label className="ruleopt">
      <input
        type="checkbox"
        checked={M[key] === undefined ? !!defaultOn : !!M[key]}
        onChange={e => setChannelSetting(key, e.target.checked)}
      />
      <span>
        <b>{label}</b>
        <small>{hint}</small>
      </span>
    </label>
  );

  const channel = SETUP_PANES.find(x => x[0] === pane);
  const isChannel = ['email', 'whatsapp', 'sms', 'push'].includes(pane);

  return (
    <>
      <nav className="segbar" aria-label="Channel setup">
        {SETUP_PANES.map(([key, label, icon]) => (
          <button
            type="button"
            key={key}
            className={`seg ch-${key}${pane === key ? ' on' : ''}`}
            aria-current={pane === key ? 'page' : undefined}
            onClick={() => setPane(key)}
          >
            <Icon name={icon} />
            {label}
          </button>
        ))}
      </nav>

      <section className={`segpane ch-${pane}`}>
        <header className="seghead">
          <span className="segic"><Icon name={channel[2]} /></span>
          <div>
            <b>{channel[1]}</b>
            <small>
              {isChannel
                ? CHANNEL_BLURB[pane]
                : pane === 'rules'
                  ? 'How hard the workspace is allowed to push, on every channel at once.'
                  : `${mkt.optouts.length} address${mkt.optouts.length === 1 ? '' : 'es'} every campaign will skip.`}
            </small>
          </div>
        </header>

        <div className="segbody">
          {pane === 'email' ? <MailSetup /> : null}
          {isChannel ? <ChannelConfig channel={pane} /> : null}

          {pane === 'rules' ? (
            <>
              <div className="g2">
                <div className="num">
                  <label>Pause between messages</label>
                  <input
                    type="number" min="0" max="60"
                    value={M.throttle ?? 3}
                    onChange={e => setChannelSetting('throttle', Number(e.target.value))}
                  />
                  <small>seconds — a gap keeps a shared mail server from throttling you</small>
                </div>
                <div className="num">
                  <label>Daily cap per channel</label>
                  <input
                    type="number" min="1" max="5000"
                    value={M.dailyCap ?? 200}
                    onChange={e => setChannelSetting('dailyCap', Number(e.target.value))}
                  />
                  <small>messages</small>
                </div>
              </div>

              <div className="rules">
                {toggle('footer', 'Add an opt-out line to every message',
                  'Required by most providers, and the reason a message is not marked spam.', true)}
                {toggle('respectQuiet', 'Never send during quiet hours',
                  'Holds anything queued outside working hours until the morning.', true)}
                {toggle('consentOnly', 'Only send where consent is recorded',
                  'Skips anyone whose record does not name this channel. Stricter, and slower to build a list.', false)}
              </div>
            </>
          ) : null}

          {pane === 'optouts' ? (
            <>
              <p className="muted">
                An address here is skipped by every campaign on every channel, whatever the
                audience says. Type one and press Enter.
              </p>
              <OptOutEditor />
            </>
          ) : null}
        </div>
      </section>
    </>
  );
}

export default function Campaigns() {
  const { mkt, mktCtx, campaignIdeas, marketingAI } = useApp();
  const [tab, setTab] = useState('campaigns');
  const [filters, setFilters] = useState(NO_FILTERS);

  const list = mkt.campaigns;
  const shown = useMemo(
    () => applyFilters(list, filters, c => campaignStats(c, mktCtx)),
    [list, filters, mktCtx]
  );

  return (
    <>
      <MarketingTiles />

      <Card>
        <div className="tabs">
          {TABS.map(([v, label, icon]) => (
            <button type="button" key={v} className={tab === v ? 'on' : ''} onClick={() => setTab(v)}>
              <Icon name={icon} />{label}
            </button>
          ))}
        </div>

        <div className="setmain">
          {tab === 'campaigns' ? (
            <div className="set">
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
                <h4 style={{ margin: 0, flex: 1 }}>Campaigns</h4>
                <button type="button" className="btn" onClick={campaignIdeas} disabled={marketingAI.loading}>
                  <Icon name="zap" />Suggest campaigns
                </button>
              </div>

              <CampaignFilters
                all={list}
                shown={shown.length}
                value={filters}
                onChange={setFilters}
                audiences={mkt.audiences}
              />

              {marketingAI.text || marketingAI.loading ? (
                <Card style={{ marginBottom: 14 }}>
                  <CardHead icon="zap" tone="g-pink" title="Worth running this month" badge={<AIBadge />} />
                  <CardBody className="brief">
                    {marketingAI.loading
                      ? <span className="muted"><Spinner /> Reading your book…</span>
                      : <Markdown text={marketingAI.text} />}
                  </CardBody>
                </Card>
              ) : null}

              <CampaignTable list={shown} />
            </div>
          ) : tab === 'analytics' ? <Analytics /> : <ChannelSetup />}
        </div>
      </Card>
    </>
  );
}
